/**
 * Date helpers that pin all "today" calculations to Africa/Gaborone (UTC+02:00).
 *
 * The audit flagged `new Date().toISOString().split('T')[0]` as a bug because
 * it returns the UTC date — a user filing an invoice at 23:00 BWT (which is
 * 21:00 UTC the same day, but 01:00 UTC the next day after midnight) would
 * see the wrong "today" once the local clock crosses midnight.
 *
 * Use these helpers everywhere we need the *firm's* operational date.
 */

const BWT_TIMEZONE = 'Africa/Gaborone';

/**
 * Return today's date in Africa/Gaborone as a YYYY-MM-DD string.
 * Safe to use as `<input type="date" value={...} />`.
 */
export function todayInGaborone() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: BWT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-CA happens to format as YYYY-MM-DD, which is the format <input type="date"> wants.
  return fmt.format(new Date());
}

/**
 * Return a given Date (or ISO string) as a YYYY-MM-DD string in Africa/Gaborone.
 */
export function toGaboroneDateString(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: BWT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(d);
}

/**
 * Format an ISO datetime as a human-readable local time in Africa/Gaborone.
 * Example: "2026-05-13 14:32".
 */
export function formatGaboroneDateTime(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: BWT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  // en-GB returns "13/05/2026, 14:32" — normalise to "2026-05-13 14:32".
  const parts = fmt.formatToParts(d).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

export default todayInGaborone;
